import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Link,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { login, verify2FA, verifyBackup, error, clearError } = useAuth();
  const { t } = useTranslation();
  
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // 2FA state
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [twoFAToken, setTwoFAToken] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [showBackupInput, setShowBackupInput] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();
    
    try {
      const result = await login(username, password);
      
      if (result.requires2FA) {
        setTempToken(result.tempToken || null);
        setCurrentStep(1);
      } else {
        navigate('/');
      }
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempToken || !twoFAToken.trim()) return;
    
    setIsLoading(true);
    clearError();
    
    try {
      await verify2FA(twoFAToken.trim(), tempToken);
      navigate('/');
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempToken || !backupCode.trim()) return;
    
    setIsLoading(true);
    clearError();
    
    try {
      await verifyBackup(backupCode.trim(), tempToken);
      navigate('/');
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setCurrentStep(0);
    setTempToken(null);
    setTwoFAToken('');
    setBackupCode('');
    setShowBackupInput(false);
    clearError();
  };

  const steps = [t('auth.login'), t('auth.twoFactorAuth')];

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom align="center">
          {t('auth.login')}
        </Typography>

        <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentStep === 0 ? (
          // Login Step
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('auth.email')}
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
            <TextField
              fullWidth
              label={t('auth.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3 }}
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('auth.login')}
            </Button>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/forgot-password')}
                sx={{ textDecoration: 'none' }}
              >
                {t('auth.forgotPassword')}
              </Link>
            </Box>
          </form>
        ) : (
          // 2FA Step
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('auth.enterCode')}
            </Typography>
            
            {!showBackupInput ? (
              <form onSubmit={handle2FASubmit}>
                <TextField
                  fullWidth
                  label={t('auth.verifyCode')}
                  type="text"
                  value={twoFAToken}
                  onChange={(e) => setTwoFAToken(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                  }}
                  placeholder="123456"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  disabled={isLoading || !twoFAToken.trim()}
                >
                  {isLoading ? t('common.loading') : t('auth.verifyCode')}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  sx={{ mt: 1 }}
                  onClick={() => setShowBackupInput(true)}
                  disabled={isLoading}
                >
                  Use Backup Code Instead
                </Button>
              </form>
            ) : (
              <form onSubmit={handleBackupSubmit}>
                <TextField
                  fullWidth
                  label="Backup Code"
                  type="text"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  placeholder="ABC123"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  disabled={isLoading || !backupCode.trim()}
                >
                  {isLoading ? t('common.loading') : 'Verify Backup Code'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  sx={{ mt: 1 }}
                  onClick={() => setShowBackupInput(false)}
                  disabled={isLoading}
                >
                  Use 2FA Token Instead
                </Button>
              </form>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Button
              fullWidth
              variant="outlined"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              {t('common.back')} {t('auth.login')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LoginForm; 